const environmentStateGlobalxmlVersion = "1";

class EnvironmentController {
    constructor() {
        this.environment_skybox = new THREE.CubeTexture();
        this.environment_skybox_cubeuv = null;
        this.environment_skybox_cubeuv_cache = new Map();
        this.materialDefines = {};
    }

    async loadEnvironment() {
        wv.effectController.environment_texture_path = "images/new_environment/";

        if (wv.settings.environmentID) {
            wv.effectController.environment_texture_path = "environment/" + wv.settings.environmentID + "/";
        }

        let loader = new THREE.TextureLoader();
        loader.setPath(wv.effectController.environment_texture_path);

        const progressBarPositionY = getProgressBarPositionY();
        let progressBar = new VRProgressBar({top: progressBarPositionY}, {
            width: 300,
            height: 60
        }, {isEnabledProgressStatus: true});
        progressBar.setStatus("Loading environment...");
        progressBar.type = progressBarTypeEnum.ENVIRONMENT;
        wv.allProgressBars[progressBarTypeEnum.ENVIRONMENT] = progressBar;

        let totalProgress = 0;

        function loadSync(path) {
            return new Promise(function (resolve, reject) {
                loader.load(path, function (texture) {
                    totalProgress += 100 / 6;
                    progressBar.setProgress(totalProgress);
                    if (parseInt(totalProgress) === 100) {
                        environmentLoadCompleted();
                    }
                    resolve(texture.image);
                }, undefined, reject)
            });
        }

        try {
            this.environment_skybox.image = [
                await loadSync('right'),
                await loadSync('left'),
                await loadSync('top'),
                await loadSync('bottom'),
                await loadSync('front'),
                await loadSync('back')
            ];
        } catch (error) {
            VRMessageController.addMessage({
                text: "Environment Loading Failed. Error: " + error,
                priority: VrMessagePriorityEnum.ERROR
            });
        }

        progressBar.fadeOut(2000);

        this.environment_skybox.needsUpdate = true;
        this.disposeEnvSkyBoxCubeUVCache();

        function environmentLoadCompleted() {
            progressBar.setStatus("Load completed");
            progressBar.setProgress(100);
            setTimeout(() => {
                progressBar = disposeProgressBar(progressBar);
            }, 2000);
        }

        await this.loadEnvSkyBoxCubeUV();
    }

    async loadEnvSkyBoxCubeUV() {
        const renderer = iq3Renderer.getRenderer();
        const cachedCubeUV = this.environment_skybox_cubeuv_cache.get(renderer);
        if (cachedCubeUV?.texture) {
            this.environment_skybox_cubeuv = cachedCubeUV.texture;
        } else {
            const cubeUVMaps = new WebGLCubeUVMaps(renderer);
            this.environment_skybox_cubeuv = cubeUVMaps.get(this.environment_skybox);
            this.environment_skybox_cubeuv_cache.set(renderer, {
                cubeUVMaps: cubeUVMaps,
                texture: this.environment_skybox_cubeuv
            });
        }

        if (!this.environment_skybox_cubeuv) {
            return;
        }

        this.environment_skybox_cubeuv.needsPMREMUpdate = true;
        this.environment_skybox_cubeuv.needsUpdate = true;

        const cubeUVSizes = this.generateCubeUVSize(this.environment_skybox_cubeuv.image.height);
        this.materialDefines["IQ3_ENVMAP_TYPE_CUBE_UV"] = "";
        this.materialDefines["IQ3_CUBEUV_MAX_MIP"] = cubeUVSizes.maxMip.toFixed(1);
        this.materialDefines["IQ3_CUBEUV_TEXEL_WIDTH"] = cubeUVSizes.texelWidth;
        this.materialDefines["IQ3_CUBEUV_TEXEL_HEIGHT"] = cubeUVSizes.texelHeight;
    }

    disposeEnvSkyBoxCubeUVCache() {
        this.environment_skybox_cubeuv_cache.forEach(cachedCubeUV => {
            cachedCubeUV.texture?.dispose();
            cachedCubeUV.cubeUVMaps?.dispose();
        });

        this.environment_skybox_cubeuv_cache.clear();
        this.environment_skybox_cubeuv = null;
    }

    change_environment(value) {
        wv.effectController.Environment = value;
        checkBoxSyncVR('Environment', value);
        if (value) {
            VR.scene.background = this.environment_skybox;
        } else {
            VR.scene.background = null;
            if (iq3XRSession.mode !== SessionMode.AR || !iq3XRSession.isSessionRunning()) {
                processReflectionStatus(false);
                processIorStatus(false);
            }
        }
    }

    saveState(document, rootObject) {
        const environmentDataNode = document.createElement("EnvironmentData");
        environmentDataNode.setAttribute("version", stateGlobalxmlVersion);
        const environmentVisibilityNode = document.createElement("environmentVisibility");
        environmentVisibilityNode.setAttribute("enabled", wv.effectController.Environment);

        environmentDataNode.appendChild(environmentVisibilityNode);
        rootObject.appendChild(environmentDataNode);
    }

    loadState(object) {
        if (!object.hasOwnProperty("EnvironmentData")) {
            return;
        }

        const environmentDataNode = object.EnvironmentData[0];
        const version = environmentDataNode.$.version;
        if (version !== environmentStateGlobalxmlVersion) {
            VRMessageController.addMessage({text: "Version conflict in environment state data"});
            return;
        }

        if (environmentDataNode.hasOwnProperty("environmentVisibility")) {
            const enabled = (environmentDataNode.environmentVisibility[0].$.enabled === "true");
            this.change_environment(enabled);
        }
    }

    generateCubeUVSize(envMapCubeUVHeight) {

        const imageHeight = envMapCubeUVHeight;

        if (imageHeight === null) return null;

        const maxMip = Math.log2(imageHeight) - 2;

        const texelHeight = 1.0 / imageHeight;

        const texelWidth = 1.0 / (3 * Math.max(Math.pow(2, maxMip), 7 * 16));

        return {texelWidth, texelHeight, maxMip};
    }
}

function WebGLCubeUVMaps(renderer) {

    let cubeUVmaps = new WeakMap();

    let pmremGenerator = null;

    function get(texture) {

        if (texture && texture.isTexture) {

            const mapping = texture.mapping;

            const isEquirectMap = (mapping === THREE.EquirectangularReflectionMapping || mapping === THREE.EquirectangularRefractionMapping);
            const isCubeMap = (mapping === THREE.CubeReflectionMapping || mapping === THREE.CubeRefractionMapping);

            // equirect/cube map to cubeUV conversion

            if (isEquirectMap || isCubeMap) {

                let renderTarget = cubeUVmaps.get(texture);

                const currentPMREMVersion = renderTarget !== undefined ? renderTarget.texture.pmremVersion : 0;

                if (texture.isRenderTargetTexture && texture.pmremVersion !== currentPMREMVersion) {

                    if (pmremGenerator === null) pmremGenerator = new THREE.PMREMGenerator(renderer);

                    renderTarget = isEquirectMap ? pmremGenerator.fromEquirectangular(texture, renderTarget) : pmremGenerator.fromCubemap(texture, renderTarget);
                    renderTarget.texture.pmremVersion = texture.pmremVersion;

                    cubeUVmaps.set(texture, renderTarget);

                    return renderTarget.texture;

                } else {

                    if (renderTarget !== undefined) {

                        return renderTarget.texture;

                    } else {

                        const image = texture.image;

                        if ((isEquirectMap && image && image.height > 0) || (isCubeMap && image && isCubeTextureComplete(image))) {

                            if (pmremGenerator === null) pmremGenerator = new THREE.PMREMGenerator(renderer);

                            renderTarget = isEquirectMap ? pmremGenerator.fromEquirectangular(texture) : pmremGenerator.fromCubemap(texture);
                            renderTarget.texture.pmremVersion = texture.pmremVersion;

                            cubeUVmaps.set(texture, renderTarget);

                            texture.addEventListener('dispose', onTextureDispose);

                            return renderTarget.texture;

                        } else {

                            // image not yet ready. try the conversion next frame

                            return null;

                        }

                    }

                }

            }

        }

        return texture;

    }

    function isCubeTextureComplete(image) {

        let count = 0;
        const length = 6;

        for (let i = 0; i < length; i++) {

            if (image[i] !== undefined) count++;

        }

        return count === length;


    }

    function onTextureDispose(event) {

        const texture = event.target;

        texture.removeEventListener('dispose', onTextureDispose);

        const cubemapUV = cubeUVmaps.get(texture);

        if (cubemapUV !== undefined) {

            cubeUVmaps.delete(texture);
            cubemapUV.dispose();

        }

    }

    function dispose() {

        cubeUVmaps = new WeakMap();

        if (pmremGenerator !== null) {

            pmremGenerator.dispose();
            pmremGenerator = null;

        }

    }

    return {
        get: get,
        dispose: dispose
    };

}

export default new EnvironmentController();
